import React, { useCallback } from "react";
import Header from "../../components/Header";
import { useLocation } from "react-router-dom";
import { getPartnerRoute, ROUTE_TEMPLATE } from "modules/navigation/routes";
import { shallowEqual, useDispatch } from "react-redux";
import { changeState, logOut } from "modules/auth/actions";
import PaymentScreenHeader from "screens/payment/PaymentScreenHeader";
import MenuLink from "components/Header/MenuLink";
import { isAdmin, isDraftingEnabled, isGlobalAdmin } from "modules/auth/helper";
import { useSelector } from "redux/reducer";
import ProductTailoringScreenHeader from "screens/productTailoring/ProductTailoringScreenHeader";
import {
  isPath,
  go,
  navigateToGlobalLanding,
  navigateToPartnerLanding,
} from "modules/navigation/helpers/navigator";
import { PartnerContextProvider } from "modules/partner/context/PartnerContext";
import SigningScreenHeader from "screens/signingScreen/SigningScreenHeader";
import SearchScreenHeader from "./SearchScreen/SearchScreenHeader";
import { getAllPartnerIDs } from "modules/auth/helper";
import { partnerSelected, partnerUnselected } from "modules/partner/actions";
import { buildHeaderLinks } from "utils/buildHeaderLinks";
import { showPartnerEntryModal } from "modules/partner/components/PartnerEntryModal";
import { showModalConfirm, ModalType } from "components/Modal";
import { UserPartner } from "modules/auth/UserProfile";
import persistentStore from "modules/localStore";
import { LOCAL_STORE_SEARCH_PARTNERS } from "modules/partner/constants";

type StoredSearchPartner = {
  id: string;
  jurisdiction?: string[];
  name: string;
};

/** Show Medicaid links when the law firm has medicaid enabled and jurisdiction is in scope. */
const showMedicaidInCatalog = (
  partner: { data: { medicaid?: boolean; jurisdiction?: string[] } } | null,
): boolean => (partner ? Boolean(partner && partner.data.medicaid) : true);
const isSensitiveFirmSwitchRoute = (location: ReturnType<typeof useLocation>) =>
  isPath(ROUTE_TEMPLATE.WORKFLOW_DETAILS, location, false) ||
  isPath(ROUTE_TEMPLATE.WORKFLOW_PAYMENT, location, false) ||
  isPath(ROUTE_TEMPLATE.WORKFLOW_SIGNING, location, false) ||
  isPath(ROUTE_TEMPLATE.PRODUCT_TAILORING, location, false) ||
  isPath(ROUTE_TEMPLATE.PLAN_TAILORING, location, false) ||
  isPath(ROUTE_TEMPLATE.DRAFTING, location, false);

const PageHeaderWrapper: React.FunctionComponent = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    user,
    incompleteProfile,
    isLoadingUserDetails,
    isLoadingPartner,
    isSessionRehydrated,
    partner,
    state,
  } = useSelector((state) => state, shallowEqual);
  const admin = isAdmin(partner?.id, user);
  const draftingAdmin = admin && isDraftingEnabled(partner);

  const globalAdmin = isGlobalAdmin(user);
  const createLink = (
    label: string,
    route: ROUTE_TEMPLATE | string,
    icon?: string,
    alert?: number,
  ): MenuLink => ({
    label,
    onClick: () => go(dispatch, route),
    isActive: isPath(route as ROUTE_TEMPLATE, undefined, false),
    icon,
    alert,
  });

  const handleLogOut = useCallback(() => dispatch(logOut()), [dispatch]);

  const handleLogIn = useCallback(
    () => go(dispatch, ROUTE_TEMPLATE.LOGIN),
    [dispatch],
  );

  const navigateHome = useCallback(() => {
    if (incompleteProfile) return;
    if (!user) {
      if (partner === null || isPath(ROUTE_TEMPLATE.PARTNER_ROOT)) {
        partner && dispatch(partnerUnselected(partner.id));
        navigateToGlobalLanding(dispatch);
      } else {
        navigateToPartnerLanding(dispatch, partner.id);
      }
      return;
    }

    go(
      dispatch,
      partner && !isPath(ROUTE_TEMPLATE.MY_PARTNERS)
        ? getPartnerRoute(partner.id, ROUTE_TEMPLATE.WORKFLOW)
        : ROUTE_TEMPLATE.WORKFLOW,
    );
  }, [dispatch, incompleteProfile, partner, user]);

  const onLogoClickedInSearchPage = useCallback(() => {
    if (partner) {
      if (user) {
        go(dispatch, getPartnerRoute(partner.id, ROUTE_TEMPLATE.WORKFLOW));
      } else {
        navigateToPartnerLanding(dispatch, partner.id);
      }
    } else {
      navigateToGlobalLanding(dispatch);
    }
  }, [dispatch, partner, user]);

  const handleChangeState = useCallback(
    () => dispatch(changeState()),
    [dispatch],
  );

  const handleChangePartner = useCallback(() => {
    const activePartners = user?.activePartners || {};
    const storedSearchPartners = persistentStore.get(
      LOCAL_STORE_SEARCH_PARTNERS,
    );
    const stateFilteredSearchPartners: Record<string, UserPartner> =
      Array.isArray(storedSearchPartners)
        ? storedSearchPartners
            .filter(
              (storedPartner: StoredSearchPartner) =>
                !state || storedPartner.jurisdiction?.includes(state),
            )
            .reduce(
              (
                result: Record<string, UserPartner>,
                storedPartner: StoredSearchPartner,
              ) => {
                result[storedPartner.id] = {
                  id: storedPartner.id,
                  name: storedPartner.name,
                  roles: [],
                };
                return result;
              },
              {},
            )
        : {};
    const fallbackPartner = partner?.id
      ? {
          [partner.id]: {
            id: partner.id,
            name: partner?.data.displayName || partner.id,
            roles: [],
          },
        }
      : {};
    const partners: Record<string, UserPartner> =
      Object.keys(activePartners).length > 0
        ? activePartners
        : Object.keys(stateFilteredSearchPartners).length > 0
          ? stateFilteredSearchPartners
          : fallbackPartner;

    if (!Object.keys(partners).length) return;

    showPartnerEntryModal(
      async (partnerId: string) => {
        const switchPartner = () => dispatch(partnerSelected(partnerId));

        if (!isSensitiveFirmSwitchRoute(location)) {
          switchPartner();
          return;
        }

        showModalConfirm({
          type: ModalType.WARNING,
          title: "Switch law firm?",
          text: "Switching firms will leave your current in-progress page and load the selected firm's workspace.",
          labelConfirm: "Switch",
          onConfirm: async () => {
            switchPartner();
          },
        });
      },
      partners,
      partner?.id,
    );
  }, [
    dispatch,
    location,
    partner?.data.displayName,
    partner?.id,
    state,
    user?.activePartners,
  ]);

  const adminLinks = [
    createLink("Documents", ROUTE_TEMPLATE.WORKFLOW),
    ...(showMedicaidInCatalog(partner)
      ? [
          createLink(
            "Medicaid Leads",
            partner
              ? ROUTE_TEMPLATE.PARTNER_MEDICAID_LEADS
              : ROUTE_TEMPLATE.MY_PARTNERS,
          ),
        ]
      : []),
    createLink("Site Setup", ROUTE_TEMPLATE.SITE_CONFIGURATION),
    createLink("Product Setup", ROUTE_TEMPLATE.PRODUCT_CONFIGURATION),
    createLink("Permissions", ROUTE_TEMPLATE.PERMISSION_CONFIGURATION),
  ];

  const draftingLinks = [createLink("Drafting", ROUTE_TEMPLATE.DRAFTING)];

  const userLinks = [
    createLink(
      "My Documents",
      partner
        ? getPartnerRoute(partner.id, ROUTE_TEMPLATE.WORKFLOW)
        : ROUTE_TEMPLATE.MY_PARTNERS,
    ),
    ...(showMedicaidInCatalog(partner)
      ? [
          createLink(
            "Medicaid Qualifications",
            partner
              ? getPartnerRoute(
                  partner!.id,
                  ROUTE_TEMPLATE.PARTNER_MEDICAID_LEADS,
                )
              : ROUTE_TEMPLATE.MY_PARTNERS,
          ),
        ]
      : []),
  ];

  const globalAdminLinks = [
    createLink("Partners", ROUTE_TEMPLATE.ADMIN_PARTNERS),
    createLink("Partner Registrations", ROUTE_TEMPLATE.ADMIN_REGISTRATIONS),
    createLink("Finance", ROUTE_TEMPLATE.ADMIN_FINANCE),
    createLink("Medicaid", ROUTE_TEMPLATE.ADMIN_MEDICAID_VARIABLES),
  ];

  const standardLinks = [
    createLink("About Us", ROUTE_TEMPLATE.ABOUT_US),
    createLink("Learning Center", ROUTE_TEMPLATE.LEARNING_CENTER),
    createLink("FAQs", ROUTE_TEMPLATE.FAQS),
  ];

  const headerLinks = buildHeaderLinks({
    globalAdmin,
    globalAdminLinks,
    draftingAdmin,
    draftingAdminLinks: draftingLinks,
    admin,
    adminLinks,
    loggedIn: user !== null,
    userLinks,
    standardLinks,
  });

  const assignedPartnerIDs = getAllPartnerIDs(user);
  const activeRegistrations = Object.values(user?.partnerRegistrations || {});
  const totalPartners = assignedPartnerIDs.size + activeRegistrations.length;
  const storedSearchPartners = persistentStore.get(LOCAL_STORE_SEARCH_PARTNERS);
  const hasSearchPartnersForState = Array.isArray(storedSearchPartners)
    ? storedSearchPartners.some(
        (storedPartner: StoredSearchPartner) =>
          !state || storedPartner.jurisdiction?.includes(state),
      )
    : false;
  const shouldShowFirmSwitcher =
    Boolean(user) &&
    !admin &&
    !globalAdmin &&
    (assignedPartnerIDs.size > 0 ||
      Boolean(partner?.id) ||
      hasSearchPartnersForState);
  const selectedPartnerLabel =
    partner?.data.displayName ||
    (partner?.id ? user?.activePartners?.[partner.id]?.name : undefined) ||
    partner?.id ||
    "Select Law Firm";

  const profileLinks: MenuLink[] = [
    createLink("My Profile", ROUTE_TEMPLATE.PROFILE, "user"),

    ...(totalPartners > 1
      ? [createLink("My Law Firms", ROUTE_TEMPLATE.MY_PARTNERS, "users")]
      : []),

    ...(totalPartners === 1 && (!partner || !assignedPartnerIDs.has(partner.id))
      ? [createLink("My Law Firm", ROUTE_TEMPLATE.MY_PARTNERS, "users")]
      : []),
  ];

  if (isPath(ROUTE_TEMPLATE.SEARCH, location))
    return (
      <SearchScreenHeader
        onLogin={handleLogIn}
        onLogout={handleLogOut}
        onNavigateHome={onLogoClickedInSearchPage}
        onPartnerChange={
          shouldShowFirmSwitcher ? handleChangePartner : undefined
        }
        profileLinks={profileLinks}
        mainNavigationLinks={headerLinks}
        selectedPartnerLabel={
          shouldShowFirmSwitcher ? selectedPartnerLabel : undefined
        }
        user={user}
      />
    );

  if (isPath(ROUTE_TEMPLATE.WORKFLOW_PAYMENT, location))
    return <PaymentScreenHeader onHome={navigateHome} />;

  if (isPath(ROUTE_TEMPLATE.WORKFLOW_SIGNING, location))
    return <SigningScreenHeader onHome={navigateHome} />;

  if (
    isPath(ROUTE_TEMPLATE.PRODUCT_TAILORING, location) ||
    isPath(ROUTE_TEMPLATE.PLAN_TAILORING, location)
  )
    return <ProductTailoringScreenHeader onHome={navigateHome} />;

  return (
    <PartnerContextProvider>
      <Header
        hideNavigationMenu={
          incompleteProfile ||
          isLoadingUserDetails ||
          isPath(ROUTE_TEMPLATE.MY_PARTNERS, location)
        }
        hideProfileMenu={
          isLoadingUserDetails || isLoadingPartner || !isSessionRehydrated
        }
        mainNavigationLinks={headerLinks}
        onLogin={handleLogIn}
        onLogoClicked={navigateHome}
        onLogout={handleLogOut}
        onStateChange={handleChangeState}
        onPartnerChange={
          shouldShowFirmSwitcher ? handleChangePartner : undefined
        }
        profileMenuLinks={profileLinks}
        selectedState={!admin && !globalAdmin && state ? state : undefined}
        selectedPartnerLabel={
          shouldShowFirmSwitcher ? selectedPartnerLabel : undefined
        }
        user={user}
        greenBorderBottom={
          isPath(ROUTE_TEMPLATE.ROOT, location) ||
          isPath(ROUTE_TEMPLATE.PARTNER_ROOT, location)
        }
      />
    </PartnerContextProvider>
  );
};

export default PageHeaderWrapper;
